/**
 * Dynamic Pricing utilities.
 *
 * Allows listing prices to vary based on:
 *   - Date ranges (e.g., peak season)
 *   - Day of week (e.g., weekend surcharge)
 *   - Demand multipliers (set via admin API)
 *
 * Rules stored in listing.publicData.dynamicPricingRules[] = [
 *   {
 *     id: string,
 *     type: 'dateRange' | 'dayOfWeek' | 'multiplier',
 *     label: string,
 *     // dateRange:
 *     startDate?: string (ISO),
 *     endDate?: string (ISO),
 *     // dayOfWeek:
 *     days?: number[] (0=Sun, 1=Mon, ..., 6=Sat),
 *     // all types:
 *     priceModifier: { type: 'percent' | 'fixed', value: number }
 *       percent: +20 = 20% more, -10 = 10% less
 *       fixed: +500 = +5.00 EUR, -100 = -1.00 EUR (in cents)
 *   }
 * ]
 *
 * Multiple rules can apply; they are applied in order (additive).
 */

export const RULE_TYPES = ['dateRange', 'dayOfWeek', 'multiplier'];
export const MODIFIER_TYPES = ['percent', 'fixed'];

/**
 * Check if a rule applies for a given date.
 * @param {Object} rule
 * @param {Date} date
 * @returns {boolean}
 */
export const ruleApplies = (rule, date) => {
  if (!rule || !date) return false;

  if (rule.type === 'dateRange') {
    const start = rule.startDate ? new Date(rule.startDate) : null;
    const end = rule.endDate ? new Date(rule.endDate) : null;
    if (start && date < start) return false;
    if (end && date > end) return false;
    return true;
  }

  if (rule.type === 'dayOfWeek') {
    return Array.isArray(rule.days) && rule.days.includes(date.getDay());
  }

  if (rule.type === 'multiplier') return true;

  return false;
};

/**
 * Apply a single price modifier to a base price.
 * @param {number} baseCents — base price in cents
 * @param {{ type: string, value: number }} modifier
 * @returns {number} — adjusted price in cents
 */
export const applyModifier = (baseCents, modifier) => {
  if (!modifier) return baseCents;
  if (modifier.type === 'percent') {
    return Math.round(baseCents * (1 + modifier.value / 100));
  }
  if (modifier.type === 'fixed') {
    return Math.max(0, baseCents + modifier.value);
  }
  return baseCents;
};

/**
 * Calculate the effective price for a given date.
 * @param {number} basePriceCents — original listing price in cents
 * @param {Object[]} rules — dynamic pricing rules from publicData
 * @param {Date|string} [date] — date to evaluate (default: today)
 * @returns {{ priceCents: number, appliedRules: Object[] }}
 */
export const getEffectivePrice = (basePriceCents, rules, date) => {
  if (!Array.isArray(rules) || rules.length === 0) {
    return { priceCents: basePriceCents, appliedRules: [] };
  }

  const evalDate = date ? new Date(date) : new Date();
  let priceCents = basePriceCents;
  const appliedRules = [];

  for (const rule of rules) {
    if (ruleApplies(rule, evalDate)) {
      priceCents = applyModifier(priceCents, rule.priceModifier);
      appliedRules.push(rule);
    }
  }

  return { priceCents: Math.max(0, priceCents), appliedRules };
};

/**
 * Validate a dynamic pricing rule.
 * @param {Object} rule
 * @returns {string|null} — error message or null
 */
export const validateRule = (rule) => {
  if (!RULE_TYPES.includes(rule.type)) {
    return `Invalid rule type: ${rule.type}`;
  }
  if (!rule.priceModifier || !MODIFIER_TYPES.includes(rule.priceModifier.type)) {
    return 'Invalid price modifier type';
  }
  if (typeof rule.priceModifier.value !== 'number') {
    return 'Price modifier value must be a number';
  }
  if (rule.type === 'percent' && (rule.priceModifier.value < -100 || rule.priceModifier.value > 500)) {
    return 'Percent modifier must be between -100 and 500';
  }
  if (rule.type === 'dayOfWeek' && (!Array.isArray(rule.days) || rule.days.length === 0)) {
    return 'dayOfWeek rule must have at least one day';
  }
  return null;
};
